import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Modal, Backdrop, Fade, Button, CircularProgress, IconButton, Tooltip, makeStyles } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import CheckIcon from "@material-ui/icons/Check";
import PriorityHighIcon from "@material-ui/icons/PriorityHigh";
import DescriptionOutlinedIcon from "@material-ui/icons/DescriptionOutlined";
import AutorenewIcon from "@material-ui/icons/Autorenew";
import PictureAsPdfOutlinedIcon from "@material-ui/icons/PictureAsPdfOutlined";
import CodeOutlinedIcon from "@material-ui/icons/CodeOutlined";
import ButtonBB from "@View/components/buttons/ButtonBB";
import { useCTAButtonStyle } from "@View/style/components/tooltipStyles";
import { downloadFile } from "@Dao/api";
import { getRoleSafe } from "@Security/stateHelpers";
import getAPI from "@API/index";

const POLL_INTERVAL_MS = 3000;

const BLUE = "#6FA4F2";
const GREEN = "#3DA66B";
const RED = "rgba(164, 36, 28, 1)";
const MUTED = "#A1ADBA";
const BORDER = "#E1E6F1";

const useStyles = makeStyles(() => ({
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  paper: {
    backgroundColor: "#fff",
    borderRadius: "12px",
    width: "100%",
    maxWidth: "460px",
    position: "relative",
    outline: "none",
    overflow: "hidden",
  },
  header: {
    padding: "22px 30px 16px",
    borderBottom: `1px solid ${BORDER}`,
  },
  closeBtn: {
    position: "absolute",
    right: "20px",
    top: "20px",
    cursor: "pointer",
    color: "#8BA0B6",
  },
  title: {
    margin: 0,
    fontWeight: 600,
    fontSize: "20px",
    lineHeight: "24px",
    color: "#1A1E24",
  },
  subtitle: {
    margin: "4px 0 0",
    fontWeight: 500,
    fontSize: "14px",
    lineHeight: "17px",
    color: MUTED,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  body: {
    padding: "20px 30px 8px",
  },
  // ── stepper (компактная шапка-полоса) ─────
  stepper: {
    display: "flex",
    alignItems: "center",
    padding: "12px 30px",
    background: "#FAFBFC",
    borderBottom: `1px solid ${BORDER}`,
  },
  step: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flex: "0 0 auto",
  },
  circle: {
    width: "24px",
    height: "24px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: `2px solid ${BORDER}`,
    color: MUTED,
    background: "#fff",
    flexShrink: 0,
    transition: "all .2s ease",
  },
  circleDone: {
    borderColor: BLUE,
    background: BLUE,
    color: "#fff",
  },
  circleActive: {
    borderColor: BLUE,
    color: BLUE,
    boxShadow: `0 0 0 3px rgba(111, 164, 242, 0.15)`,
  },
  circleSuccess: {
    borderColor: GREEN,
    background: GREEN,
    color: "#fff",
  },
  circleError: {
    borderColor: RED,
    background: RED,
    color: "#fff",
  },
  stepLabel: {
    fontWeight: 500,
    fontSize: "12px",
    lineHeight: "15px",
    whiteSpace: "nowrap",
    color: MUTED,
  },
  stepLabelActive: {
    color: "#1A1E24",
  },
  connector: {
    flex: 1,
    height: "2px",
    minWidth: "12px",
    background: BORDER,
    margin: "0 8px",
    borderRadius: "1px",
    transition: "background .2s ease",
  },
  connectorFilled: {
    background: BLUE,
  },
  // ── info rows ─────────────────────────────
  infoRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "13px 0",
    borderBottom: `1px solid ${BORDER}`,
  },
  infoLabel: {
    fontWeight: 500,
    fontSize: "13px",
    lineHeight: "16px",
    color: MUTED,
  },
  infoValue: {
    fontWeight: 600,
    fontSize: "13px",
    lineHeight: "16px",
    color: "#1A1E24",
  },
  statusPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "4px 10px",
    borderRadius: "20px",
    fontWeight: 600,
    fontSize: "12px",
    lineHeight: "15px",
  },
  errorBox: {
    marginTop: "16px",
    background: "rgba(164, 36, 28, 0.06)",
    border: `1px solid rgba(164, 36, 28, 0.25)`,
    borderRadius: "8px",
    padding: "12px 14px",
    fontSize: "13px",
    fontWeight: 500,
    lineHeight: "18px",
    color: RED,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  // ── footer ────────────────────────────────
  footer: {
    display: "flex",
    gap: "10px",
    padding: "20px 30px 26px",
  },
  ctaBtn: {
    flex: 1,
    borderRadius: "8px",
    fontWeight: 600,
    fontSize: "14px",
    lineHeight: "17px",
    padding: "14px 18px",
    textTransform: "none",
  },
}));

const formatDate = (value) =>
  value ? new Date(value).toLocaleString("ru-RU") : "—";

const PrecreditReportModal = ({ lead, open, handleClose }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const ctaStyle = useCTAButtonStyle();

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [starting, setStarting] = useState(false);
  const pollRef = useRef(null);

  const isAdmin = getRoleSafe() === "Admin";

  const isInProgress =
    report != null && (report.status === "New" || report.status === "InProgress");
  const isCompleted = report != null && report.status === "Completed";
  const isNotStarted = report == null;
  const isSuccess = isCompleted && report?.success;
  const isFailed = isCompleted && !report?.success;

  // 0 — не начат, 1 — формируется, 2 — результат
  const activeIndex = isCompleted ? 2 : isInProgress ? 1 : 0;

  const clearPoll = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const fetchReport = (leadId, { withSpinner } = {}) => {
    if (withSpinner) setLoading(true);
    return getAPI()
      .request({ method: "get", url: `/api/lead/${leadId}/precredit-report`, suppressError: true, suppressLoading: true })
      .then((response) => {
        const data = response?.data ?? null;
        setReport(data);
        return data;
      })
      .catch(() => null)
      .finally(() => {
        if (withSpinner) setLoading(false);
      });
  };

  // Загрузка статуса при открытии модалки.
  useEffect(() => {
    if (!open || !lead?.id) {
      setReport(null);
      clearPoll();
      return;
    }
    fetchReport(lead.id, { withSpinner: true });
    return clearPoll;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, lead?.id]);

  // Поллинг, пока отчёт формируется.
  useEffect(() => {
    if (!open || !lead?.id) return;
    if (isInProgress && !pollRef.current) {
      pollRef.current = setInterval(() => {
        fetchReport(lead.id).then((data) => {
          if (!data || data.status === "Completed") clearPoll();
        });
      }, POLL_INTERVAL_MS);
    }
    if (!isInProgress) clearPoll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, lead?.id, isInProgress]);

  const handleStart = () => {
    if (!lead?.id) return;
    setStarting(true);
    getAPI()
      .request({ method: "post", url: `/api/lead/${lead.id}/precredit-report`, suppressError: false, suppressLoading: true })
      .then(() => fetchReport(lead.id))
      .catch(() => null)
      .finally(() => setStarting(false));
  };

  const handleExportPdf = () => {
    if (!lead?.id) return;
    downloadFile(`/api/lead/${lead.id}/precredit-report/pdf`);
  };

  const handleExportRawJson = () => {
    if (!report?.id) return;
    downloadFile(`/api/lead/precredit-report/${report.id}/raw`);
  };

  if (!lead) return null;

  const statusText = isCompleted
    ? t(isSuccess ? "precreditReportModal.status.Success" : "precreditReportModal.status.Failed")
    : t(`precreditReportModal.status.${report?.status ?? "NotStarted"}`,
        t("precreditReportModal.status.NotStarted"));

  const statusPillStyle = isSuccess
    ? { background: "rgba(61, 166, 107, 0.12)", color: GREEN }
    : isFailed
      ? { background: "rgba(164, 36, 28, 0.1)", color: RED }
      : isInProgress
        ? { background: "rgba(111, 164, 242, 0.14)", color: BLUE }
        : { background: "#F3F5F7", color: "#54606F" };

  const stages = [
    {
      label: t("precreditReportModal.stages.created"),
      icon: <DescriptionOutlinedIcon style={{ fontSize: 14 }} />,
    },
    {
      label: t("precreditReportModal.stages.generating"),
      icon: <AutorenewIcon style={{ fontSize: 14 }} />,
    },
    {
      label: t("precreditReportModal.stages.result"),
      icon: <CheckIcon style={{ fontSize: 14 }} />,
    },
  ];

  const renderCircle = (index) => {
    // последняя стадия (результат) при завершении: успех/ошибка
    if (index === 2 && isCompleted) {
      return (
        <div className={`${classes.circle} ${isSuccess ? classes.circleSuccess : classes.circleError}`}>
          {isSuccess ? <CheckIcon style={{ fontSize: 15 }} /> : <PriorityHighIcon style={{ fontSize: 15 }} />}
        </div>
      );
    }
    if (index < activeIndex) {
      return (
        <div className={`${classes.circle} ${classes.circleDone}`}>
          <CheckIcon style={{ fontSize: 15 }} />
        </div>
      );
    }
    if (index === activeIndex) {
      return (
        <div className={`${classes.circle} ${classes.circleActive}`}>
          {isInProgress && index === 1
            ? <CircularProgress size={12} style={{ color: BLUE }} />
            : stages[index].icon}
        </div>
      );
    }
    return <div className={classes.circle}>{stages[index].icon}</div>;
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      className={classes.modal}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 300 }}
    >
      <Fade in={open}>
        <div className={classes.paper}>
          <div className={classes.header}>
            <div className={classes.closeBtn} onClick={handleClose}>
              <CloseIcon fontSize="small" />
            </div>
            <h2 className={classes.title}>{t("precreditReportModal.title")}</h2>
            {lead.borrowerName && <p className={classes.subtitle}>{lead.borrowerName}</p>}
          </div>

          <div className={classes.stepper}>
            {stages.map((stage, index) => (
              <React.Fragment key={index}>
                <div className={classes.step}>
                  {renderCircle(index)}
                  <span
                    className={`${classes.stepLabel} ${index <= activeIndex ? classes.stepLabelActive : ""}`}
                  >
                    {stage.label}
                  </span>
                </div>
                {index < stages.length - 1 && (
                  <div
                    className={`${classes.connector} ${index < activeIndex ? classes.connectorFilled : ""}`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>

          <div className={classes.body}>
            {loading ? (
              <div style={{ display: "flex", justifyContent: "center", padding: "20px 0" }}>
                <CircularProgress size={28} />
              </div>
            ) : (
              <>
                <div className={classes.infoRow}>
                  <span className={classes.infoLabel}>{t("precreditReportModal.reportDate")}</span>
                  <span className={classes.infoValue}>
                    {formatDate(report?.finishedAt)}
                  </span>
                </div>
                <div className={classes.infoRow} style={{ borderBottom: "none" }}>
                  <span className={classes.infoLabel}>{t("precreditReportModal.statusLabel")}
                    {isCompleted && isAdmin && report?.id && (
                      <Tooltip title={t("precreditReportModal.buttons.ExportJson")}>
                        <IconButton
                          onClick={handleExportRawJson}
                          size="small"
                          style={{ color: MUTED }}
                        >
                          <CodeOutlinedIcon style={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    )}</span>                  
                  <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <span className={classes.statusPill} style={statusPillStyle}>
                      {statusText}
                    </span>                    
                  </span>
                </div>

                {isFailed && report?.errorMessage && (
                  <div className={classes.errorBox}>{report.errorMessage}</div>
                )}
              </>
            )}
          </div>

          <div className={classes.footer}>
            {(isNotStarted || isCompleted) && (
              <ButtonBB
                onClick={handleStart}
                disabled={starting || loading}
                startIcon={
                  starting
                    ? <CircularProgress size={16} />
                    : isCompleted
                      ? <AutorenewIcon style={{ fontSize: 18 }} />
                      : null
                }
                style={{ flex: 1, borderRadius: "8px", padding: "14px 18px", textTransform: "none" }}
              >
                {isCompleted
                  ? t("precreditReportModal.buttons.Recalculate")
                  : t("precreditReportModal.buttons.Generate")}
              </ButtonBB>
            )}
            {isSuccess && (
              <Button
                onClick={handleExportPdf}
                className={`${ctaStyle.default} ${classes.ctaBtn}`}
                startIcon={<PictureAsPdfOutlinedIcon style={{ fontSize: 18 }} />}
              >
                {t("precreditReportModal.buttons.ExportPdf")}
              </Button>
            )}
          </div>
        </div>
      </Fade>
    </Modal>
  );
};

export default PrecreditReportModal;
